import type { BreadcrumbItem } from '@/ui/layout/breadcrumbsTypes';

type BreadcrumbRoute = {
  path: string;
  title?: string;
  nav?: { label: string };
};

type RouteMatch = {
  route: BreadcrumbRoute;
  match: { params: Record<string, string> };
  score: number;
};

const getPathSegments = (path: string): string[] =>
  path.split('/').filter((segment): segment is string => segment.length > 0);

const getMatchScore = (path: string): number => getPathSegments(path).length;

const hasParams = (path: string): boolean => path.includes('/:');

const resolveRoutePath = (route: BreadcrumbRoute): string => route.path;

const matchRoute = (pattern: string, path: string): { params: Record<string, string> } | null => {
  const patternSegments = getPathSegments(pattern);
  const pathSegments = getPathSegments(path);
  if (patternSegments.length !== pathSegments.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index];
    const pathSegment = pathSegments[index];
    if (!patternSegment || !pathSegment) return null;
    if (patternSegment.startsWith(':')) {
      params[patternSegment.slice(1)] = pathSegment;
      continue;
    }
    if (patternSegment !== pathSegment) return null;
  }
  return { params };
};

const getBasePath = (path: string): string | null => {
  const paramIndex = path.indexOf('/:');
  if (paramIndex === -1) return null;
  return path.slice(0, paramIndex);
};

const hasBaseRoute = (routes: BreadcrumbRoute[], routePath: string): boolean => {
  const basePath = getBasePath(routePath);
  if (!basePath) return true;
  return routes.some((route) => route.path === basePath);
};

const findBestMatch = (routes: BreadcrumbRoute[], path: string): RouteMatch | null => {
  let bestMatch: RouteMatch | null = null;
  routes.forEach((route) => {
    const routePath = resolveRoutePath(route);
    const match = matchRoute(routePath, path);
    if (!match) return;
    const score = getMatchScore(routePath);
    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { route, match, score };
    }
  });
  return bestMatch;
};

const humanizeSlug = (value: string): string => {
  const decoded = decodeURIComponent(value);
  const normalized = decoded.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!normalized) return decoded;
  return normalized
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const resolveParamLabel = (params: Record<string, string>): string | null => {
  const runNumber = params.runNumber;
  if (runNumber) return `Run ${runNumber}`;
  const pipelineSlug = params.pipelineSlug;
  if (pipelineSlug) return humanizeSlug(pipelineSlug);
  return null;
};

const resolveRouteLabel = (
  route: BreadcrumbRoute,
  routePath: string,
  match: { params: Record<string, string> },
): string | null => {
  if (routePath.endsWith('/settings')) {
    return 'Settings';
  }
  const segments = getPathSegments(routePath);
  const lastSegment = segments.length > 0 ? segments[segments.length - 1] : undefined;
  const isLastSegmentParam = Boolean(lastSegment?.startsWith(':'));
  if (isLastSegmentParam) {
    const paramLabel = resolveParamLabel(match.params);
    if (paramLabel) return paramLabel;
  }
  return route.nav?.label ?? route.title ?? null;
};

export const buildBreadcrumbItems = (
  pathname: string,
  routes: BreadcrumbRoute[],
): BreadcrumbItem[] => {
  if (pathname === '/') return [];
  if (pathname.startsWith('/results-files/')) {
    const items: BreadcrumbItem[] = [{ label: 'Results Files', href: '/uploads' }];
    return items;
  }
  if (pathname.startsWith('/uploads/')) {
    const segments = getPathSegments(pathname);
    const uploadId = segments.length > 1 ? segments[1] : null;
    const items: BreadcrumbItem[] = [{ label: 'Uploads', href: '/uploads' }];
    if (uploadId) {
      items.push({ label: uploadId });
    }
    return items;
  }
  const exactBaseRoute = routes.find((route) => {
    const routePath = resolveRoutePath(route);
    return routePath === pathname && !hasParams(routePath);
  });
  if (exactBaseRoute) return [];
  const segments = getPathSegments(pathname);
  const items: BreadcrumbItem[] = [];
  let currentPath = '';
  segments.forEach((segment) => {
    currentPath = `${currentPath}/${segment}`;
    const bestMatch = findBestMatch(routes, currentPath);
    if (!bestMatch) return;
    const { route, match } = bestMatch;
    const routePath = resolveRoutePath(route);
    const titleLabel = route.nav?.label ?? route.title ?? null;
    const routeSegments = getPathSegments(routePath);
    const lastSegment =
      routeSegments.length > 0 ? routeSegments[routeSegments.length - 1] : undefined;
    const isLastSegmentParam = Boolean(lastSegment?.startsWith(':'));
    if (isLastSegmentParam) {
      const baseExists = hasBaseRoute(routes, routePath);
      if (!baseExists && titleLabel) {
        items.push({ label: titleLabel });
      }
      const paramLabel = resolveParamLabel(match.params);
      if (!paramLabel) return;
      items.push({ label: paramLabel, href: currentPath });
      return;
    }
    const label = resolveRouteLabel(route, routePath, match);
    if (!label) return;
    items.push({ label, href: currentPath });
  });
  if (items.length === 0) return items;
  const lastItem = items[items.length - 1];
  items[items.length - 1] = { label: lastItem.label };
  return items;
};
